import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { bidsAPI } from '../../services/api';

export interface BidDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
}

export interface Bid {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  category: string;
  closing_date: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  documents: BidDocument[];
}

interface BidsState {
  items: Bid[];
  current: Bid | null;
  loading: boolean;
  loadingCurrent: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: BidsState = {
  items: [],
  current: null,
  loading: false,
  loadingCurrent: false,
  submitting: false,
  error: null,
};

// ─── Thunks ────────────────────────────────────────────────────────────────

export const fetchBids = createAsyncThunk(
  'bids/fetchAll',
  async (category: string = '', { rejectWithValue }) => {
    try {
      const res = await bidsAPI.getAll(category || undefined);
      return res.data as Bid[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bids');
    }
  }
);

export const fetchBidById = createAsyncThunk(
  'bids/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await bidsAPI.getById(id);
      return res.data as Bid;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bid');
    }
  }
);

export const createBid = createAsyncThunk(
  'bids/create',
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const res = await bidsAPI.create(formData);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create bid');
    }
  }
);

export const updateBid = createAsyncThunk(
  'bids/update',
  async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
    try {
      const res = await bidsAPI.update(id, formData);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update bid');
    }
  }
);

export const deleteBid = createAsyncThunk(
  'bids/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await bidsAPI.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete bid');
    }
  }
);

export const deleteBidDocument = createAsyncThunk(
  'bids/deleteDocument',
  async ({ documentId, bidId }: { documentId: string; bidId: string }, { rejectWithValue }) => {
    try {
      await bidsAPI.deleteDocument(documentId);
      return { documentId, bidId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete document');
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────

const bidsSlice = createSlice({
  name: 'bids',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: builder => {
    // fetchBids
    builder
      .addCase(fetchBids.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBids.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchBids.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchBidById
    builder
      .addCase(fetchBidById.pending, state => {
        state.loadingCurrent = true;
        state.error = null;
        state.current = null;
      })
      .addCase(fetchBidById.fulfilled, (state, action) => {
        state.current = action.payload;
        state.loadingCurrent = false;
      })
      .addCase(fetchBidById.rejected, (state, action) => {
        state.loadingCurrent = false;
        state.error = action.payload as string;
      });

    // createBid
    builder
      .addCase(createBid.pending, state => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createBid.fulfilled, state => {
        state.submitting = false;
      })
      .addCase(createBid.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });

    // updateBid
    builder
      .addCase(updateBid.pending, state => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateBid.fulfilled, state => {
        state.submitting = false;
      })
      .addCase(updateBid.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });

    // deleteBid
    builder
      .addCase(deleteBid.fulfilled, (state, action) => {
        state.items = state.items.filter(b => b.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteBid.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // deleteBidDocument
    builder
      .addCase(deleteBidDocument.fulfilled, (state, action) => {
        const { documentId, bidId } = action.payload;
        // Update current bid's documents if loaded
        if (state.current && state.current.id === bidId) {
          state.current.documents = state.current.documents.filter(
            d => d.id !== documentId
          );
        }
        state.error = null;
      })
      .addCase(deleteBidDocument.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrent } = bidsSlice.actions;
export default bidsSlice.reducer;